/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import {AfterViewChecked, ChangeDetectorRef, Component, computed, ElementRef, inject, OnInit, signal, ViewChild, ViewEncapsulation} from '@angular/core';
import {CellValueChangedEvent, ColDef, GridReadyEvent} from 'ag-grid-community';
import {AgGridAngular} from 'ag-grid-angular';
import {finalize, take, tap} from 'rxjs';
import {EditableGridComponent} from '../editable-grid-component';
import {GridDownloadButtons} from '../grid-download-buttons';
import {CommonHelperService} from '../../service/common-helper.service';
import {ExcelService} from '../../service/excel.service';
import {ObjectDropdownCellEditorComponent} from '../cell-renderer/object-dropdown-cell-editor.component';
import {ActionsCellRendererComponent} from '../cell-renderer/actions-cell-renderer.component';
import {
    AggregatorSignalKey,
    AggregatorSignalRow,
    EioEnumOption,
    IoTypeDesignatorOption,
    PlatingOption,
    POWER_TYPES,
    PowerType,
    TerminalSizeOption
} from '../../model/aggregator-signal.models';
import {
    architecturesForObjectCode,
    DEFAULT_ICD_PLATFORM,
    distinctObjectCodes,
    ICD_PLATFORMS,
    keyExists,
    keyLabel as formatKeyLabel,
    keywordDropdownOptions,
    keywordShowClear,
    keywordsForScope,
    keywordVisibility,
    normalizeArchitecture,
    normalizeKeyword,
    powerTypesForObjectCodeArchitecture
} from './aggregator-signal-key.helpers';
import {
    handleIcdCompareKeydown,
    setExportingWithDetectChanges,
    subscribeIcdCompareDownload
} from '../../utils/icd-compare-export.util';
import {compareNaturalText} from '../../utils/natural-text.util';

@Component({
    selector: 'app-aggregator-signal-table',
    templateUrl: './aggregator-signal-table.component.html',
    styleUrls: ['./aggregator-signal-table.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class AggregatorSignalTableComponent extends EditableGridComponent implements OnInit, AfterViewChecked {
    @ViewChild(AgGridAngular, {read: ElementRef}) protected agGridHost?: ElementRef<HTMLElement>;
    paginationPageSize = 1000;
    paginationPageSizeSelector: number[] = [100, 500, 1000];
    adminOnlyColumns = ['actions'];
    private readonly downloadButtons = new GridDownloadButtons({
        canExportReport: () => this.canExportReport,
        canExportHistory: () => this.canExportHistory,
        onExportReport: () => this.exportReport(),
        onExportHistory: () => this.exportHistory()
    });

    readonly keysSignal = signal<AggregatorSignalKey[]>([]);
    readonly browseObjectCodeSignal = signal('');
    readonly browseArchitectureSignal = signal('');
    readonly browsePowerTypeSignal = signal<PowerType | ''>('');
    readonly browseKeywordSignal = signal('');
    readonly activeObjectCodeSignal = signal('');
    readonly activeArchitectureSignal = signal('');
    readonly activePowerTypeSignal = signal<PowerType | ''>('');
    readonly activeKeywordSignal = signal('');
    readonly createModeSignal = signal(false);
    readonly createDialogVisibleSignal = signal(false);
    readonly createDraftSnapshotSignal = signal<AggregatorSignalRow[]>([]);
    readonly icdComparePermission = signal(false);
    readonly dataChangedSignal = signal(false);
    readonly browseObjectCodes = computed(() => distinctObjectCodes(this.keysSignal()));
    readonly browseArchitecturesForObjectCode = computed(() =>
        architecturesForObjectCode(this.keysSignal(), this.browseObjectCodeSignal()));
    readonly browsePowerTypesForObjectCode = computed(() =>
        powerTypesForObjectCodeArchitecture(
            this.keysSignal(), this.browseObjectCodeSignal(), this.browseArchitectureSignal()));
    readonly browseKeywordOptions = computed(() => keywordDropdownOptions(keywordsForScope(
        this.keysSignal(), this.browseObjectCodeSignal(), this.browseArchitectureSignal(),
        this.browsePowerTypeSignal())));
    readonly browseKeywordVisibility = computed(() => keywordVisibility(keywordsForScope(
        this.keysSignal(), this.browseObjectCodeSignal(), this.browseArchitectureSignal(),
        this.browsePowerTypeSignal())));
    readonly browseKeywordVisible = computed(() => this.browseKeywordVisibility() !== 'hidden');
    readonly browseKeywordReadonly = computed(() => this.browseKeywordVisibility() === 'readonly');
    readonly keywordShowClear = keywordShowClear;
    readonly browseSelectorsDisabled = computed(() => this.dataChangedSignal());
    readonly hasActiveTable = computed(() =>
        !!this.activeObjectCodeSignal()
        && !!this.activeArchitectureSignal()
        && !!this.activePowerTypeSignal());
    readonly canDeleteTable = computed(() =>
        this.adminPermission()
        && !this.dataChangedSignal()
        && !this.createModeSignal()
        && !!this.activeObjectCodeSignal()
        && !!this.activeArchitectureSignal()
        && !!this.activePowerTypeSignal());
    readonly canExportHistoryAll = computed(() =>
        this.canExport() && this.adminPermission());
    readonly canOpenCreateDialog = computed(() =>
        this.keysSignal().length > 0 && !this.dataChangedSignal());
    /**
     * icdComparePermission is the Create Aggregator ICDs probe, which excludes Aggregator
     * Admin. adminPermission is the signal-table write role, which includes them and matches
     * who can already download this report from the IO Matrix page.
     */
    private readonly icdCompareActorAllowed = computed(() =>
        this.canExport()
        && (this.icdComparePermission() || this.adminPermission())
        && !this.dataChangedSignal()
        && !this.createModeSignal());
    /**
     * Signal-backed so picking a table (active key + refreshGrid) does not flip a getter
     * mid-CD. Reads rowDataSignal, not the plain rowData array — refreshGrid updates that
     * field while the template is still being checked (NG0100 on [disabled]).
     */
    readonly canExportIcdCompare = computed(() =>
        this.icdCompareActorAllowed()
        && this.hasActiveTable()
        && this.rowDataSignal().some(row => row?.state !== 'deleted'));
    private readonly changeDetectorRef = inject(ChangeDetectorRef);

    createObjectCodes: string[] = [];
    filteredCreateObjectCodes: string[] = [];
    readonly dialogPowerTypeOptions = POWER_TYPES;
    readonly dialogArchitectureOptions = [...ICD_PLATFORMS];
    dialogObjectCode = '';
    dialogArchitecture = DEFAULT_ICD_PLATFORM;
    dialogPowerType: PowerType | '' = '';
    dialogKeyword = '';
    dialogCopySourceLabel = '';

    private suppressNextPowerTypeLoad = false;
    createObjectCodesLoading = false;
    private createObjectCodesLoaded = false;
    private loadRowsRequestId = 0;

    ioTypeDesignatorOptions: IoTypeDesignatorOption[] = [];
    private readonly ioTypeNameById = new Map<number, string>();
    private readonly ioTypeIdByName = new Map<string, number>();

    terminalSizeOptions: TerminalSizeOption[] = [];
    private readonly terminalSizeNameById = new Map<number, string>();
    private readonly terminalSizeIdByName = new Map<string, number>();

    platingOptions: PlatingOption[] = [];
    private readonly platingNameById = new Map<number, string>();
    private readonly platingIdByName = new Map<string, number>();
    pwmGroupDropdownOptions: {id: string, pwmGroup: string}[] = [];
    vRefMappingDropdownOptions: {id: string, vRefMapping: string}[] = [];
    flexibleDiscreteOutputOptions: {id: string, flexibleDiscreteOutputs: string, label: string}[] = [];

    ngOnInit(): void {
        // Two-tier gate: every internal user may view, only admins may edit. Setting adminPermissionUrl
        // is what makes the inherited defaultColDef.editable render the grid read-only for viewers.
        this.permissionUrl = this.esciService.aggregatorSignalPermissionUrl;
        this.adminPermissionUrl = this.esciService.aggregatorSignalAdminPermissionUrl;
        this.saveUrl = this.esciService.saveAggregatorSignalUrl;
        this.esciService.getPermissionAndFileSizeLimit(this, false);
        this.esciService.checkExportPermission(this);
        this.esciService.checkAggregatorIoMatrixIcdPermission().subscribe({
            next: allowed => this.icdComparePermission.set(allowed === true),
            error: () => this.icdComparePermission.set(false)
        });
        this.refreshKeys().subscribe();
        this.buildColumns();
        this.loadIoTypeDesignators();
        this.loadTerminalSizes();
        this.loadPlatings();
        this.loadPwmGroups();
        this.loadVRefMappings();
        this.loadFlexibleDiscreteOutputs();
    }

    override refreshGrid(rows: any[], node?: any, onCompleteCallback?: () => void): void {
        super.refreshGrid(rows, node, onCompleteCallback);
        this.dataChangedSignal.set(this.dataChanged);
    }

    override cellEditingStopped(event: any, rows: any[]): void {
        super.cellEditingStopped(event, rows);
        this.dataChangedSignal.set(this.dataChanged);
    }

    override updateDataChanged(rows: any[]): void {
        super.updateDataChanged(rows);
        this.dataChangedSignal.set(this.dataChanged);
    }

    private setDirty(dirty: boolean): void {
        this.dataChanged = dirty;
        this.dataChangedSignal.set(dirty);
    }

    /** Create-dialog object codes are admin-gated server-side, so only ask once we know the user is one. */
    override onAdminPermissionResolved(): void {
        if (this.adminPermission()) {
            this.loadCreateObjectCodes();
        }
    }

    private loadCreateObjectCodes(): void {
        if (this.createObjectCodesLoaded || this.createObjectCodesLoading) {
            return;
        }
        this.createObjectCodesLoading = true;
        this.esciService.getAggregatorSignalCreateObjectCodes().subscribe({
            next: codes => {
                this.createObjectCodes = (codes || [])
                    .map(code => String(code ?? '').trim())
                    .filter(code => code.length > 0);
                this.createObjectCodesLoaded = true;
                this.createObjectCodesLoading = false;
                if (this.createDialogVisibleSignal()) {
                    this.filterCreateObjectCodes({query: ''});
                }
            },
            error: () => {
                this.createObjectCodesLoading = false;
                this.messageService.processError('Could not load object codes for create. Please try again.');
            }
        });
    }

    filterCreateObjectCodes(event: { query?: string }): void {
        const query = String(event?.query ?? '').trim().toLowerCase();
        if (!query) {
            this.filteredCreateObjectCodes = [...this.createObjectCodes];
            return;
        }
        this.filteredCreateObjectCodes = this.createObjectCodes.filter(code =>
            code.toLowerCase().includes(query));
    }

    get keys(): AggregatorSignalKey[] {
        return this.keysSignal();
    }

    get browseObjectCode(): string {
        return this.browseObjectCodeSignal();
    }

    set browseObjectCode(value: string) {
        this.browseObjectCodeSignal.set(value ?? '');
    }

    get browseArchitecture(): string {
        return this.browseArchitectureSignal();
    }

    set browseArchitecture(value: string) {
        this.browseArchitectureSignal.set(value ?? '');
    }

    get browsePowerType(): PowerType | '' {
        return this.browsePowerTypeSignal();
    }

    set browsePowerType(value: PowerType | '') {
        this.browsePowerTypeSignal.set(value ?? '');
    }

    get browseKeyword(): string {
        return this.browseKeywordSignal();
    }

    set browseKeyword(value: string) {
        this.browseKeywordSignal.set(normalizeKeyword(value));
    }

    /** Active editing key — used by Save, exports, and grid context label. */
    get objectCode(): string {
        return this.activeObjectCodeSignal();
    }

    set objectCode(value: string) {
        this.activeObjectCodeSignal.set(value ?? '');
    }

    get architecture(): string {
        return this.activeArchitectureSignal();
    }

    set architecture(value: string) {
        this.activeArchitectureSignal.set(normalizeArchitecture(value));
    }

    get powerType(): PowerType | '' {
        return this.activePowerTypeSignal();
    }

    set powerType(value: PowerType | '') {
        this.activePowerTypeSignal.set(value ?? '');
    }

    get keyword(): string {
        return this.activeKeywordSignal();
    }

    set keyword(value: string) {
        this.activeKeywordSignal.set(normalizeKeyword(value));
    }


    readonly allKeyLabels = computed(() => this.keysSignal().map(key => this.keyLabel(key)));

    get contextLabelPrefix(): string {
        return this.createModeSignal() ? 'Creating Aggregator Signal:' : 'Editing Aggregator Signal:';
    }

    get createDialogVisible(): boolean {
        return this.createDialogVisibleSignal();
    }

    set createDialogVisible(value: boolean) {
        this.createDialogVisibleSignal.set(!!value);
    }

    keyLabel(key: AggregatorSignalKey): string {
        return formatKeyLabel(key);
    }

    onBrowseObjectCodeChanged(): void {
        if (this.browseSelectorsDisabled()) {
            return;
        }
        this.createModeSignal.set(false);
        const allowedArchitectures = architecturesForObjectCode(this.keysSignal(), this.browseObjectCodeSignal());
        if (this.browseArchitectureSignal() && !allowedArchitectures.includes(this.browseArchitectureSignal())) {
            this.browseArchitectureSignal.set('');
        }
        if (allowedArchitectures.length === 1) {
            this.suppressNextPowerTypeLoad = true;
            this.browseArchitectureSignal.set(allowedArchitectures[0]);
            this.onBrowseArchitectureChanged();
            this.suppressNextPowerTypeLoad = false;
            return;
        }
        if (this.browseObjectCodeSignal() && this.browseArchitectureSignal()
            && allowedArchitectures.includes(this.browseArchitectureSignal())) {
            this.onBrowseArchitectureChanged();
            return;
        }
        this.browsePowerTypeSignal.set('');
        this.browseKeywordSignal.set('');
        this.clearActiveKeyGrid();
    }

    onBrowseArchitectureChanged(): void {
        if (this.browseSelectorsDisabled() && !this.suppressNextPowerTypeLoad) {
            return;
        }
        this.createModeSignal.set(false);
        const allowed = powerTypesForObjectCodeArchitecture(
            this.keysSignal(), this.browseObjectCodeSignal(), this.browseArchitectureSignal());
        const currentPowerType = this.browsePowerTypeSignal();
        if (currentPowerType && !allowed.includes(currentPowerType)) {
            this.browsePowerTypeSignal.set('');
        }
        if (allowed.length === 1) {
            this.suppressNextPowerTypeLoad = true;
            this.browsePowerTypeSignal.set(allowed[0]);
            this.reconcileBrowseKeyword();
            this.syncBrowseToActiveAndLoad();
            this.suppressNextPowerTypeLoad = false;
            return;
        }
        if (this.browseObjectCodeSignal() && this.browseArchitectureSignal() && currentPowerType
            && allowed.includes(currentPowerType)) {
            this.reconcileBrowseKeyword();
            this.syncBrowseToActiveAndLoad();
            return;
        }
        this.browseKeywordSignal.set('');
        this.clearActiveKeyGrid();
    }

    onBrowsePowerTypeChanged(): void {
        if (this.browseSelectorsDisabled() || this.suppressNextPowerTypeLoad) {
            return;
        }
        this.createModeSignal.set(false);
        this.reconcileBrowseKeyword();
        if (this.browseObjectCodeSignal() && this.browseArchitectureSignal() && this.browsePowerTypeSignal()) {
            this.syncBrowseToActiveAndLoad();
        } else {
            this.clearActiveKeyGrid();
        }
    }

    onBrowseKeywordChanged(): void {
        if (this.browseSelectorsDisabled()) {
            return;
        }
        this.createModeSignal.set(false);
        if (this.browseObjectCodeSignal() && this.browseArchitectureSignal() && this.browsePowerTypeSignal()) {
            this.syncBrowseToActiveAndLoad();
        } else {
            this.clearActiveKeyGrid();
        }
    }

    private reconcileBrowseKeyword(): void {
        const visibility = this.browseKeywordVisibility();
        const options = this.browseKeywordOptions();
        if (visibility === 'hidden') {
            this.browseKeywordSignal.set('');
            return;
        }
        if (visibility === 'readonly') {
            this.browseKeywordSignal.set(options[0] || '');
            return;
        }
        if (!options.includes(this.browseKeywordSignal())) {
            this.browseKeywordSignal.set(options.includes('') ? '' : (options[0] || ''));
        }
    }

    syncBrowseToActiveAndLoad(): void {
        const objectCode = this.browseObjectCodeSignal();
        const architecture = this.browseArchitectureSignal();
        const powerType = this.browsePowerTypeSignal();
        const keyword = this.browseKeywordSignal();
        if (!objectCode || !architecture || !powerType
            || !keyExists(this.keysSignal(), objectCode, architecture, powerType, keyword)) {
            return;
        }
        this.activeObjectCodeSignal.set(objectCode);
        this.activeArchitectureSignal.set(architecture);
        this.activePowerTypeSignal.set(powerType);
        this.activeKeywordSignal.set(keyword);
        this.loadExistingKey();
    }

    loadExistingKey(): void {
        const objectCode = this.activeObjectCodeSignal();
        const architecture = this.activeArchitectureSignal();
        const powerType = this.activePowerTypeSignal();
        if (!objectCode || !architecture || !powerType) {
            return;
        }
        const keyword = this.activeKeywordSignal();
        const requestId = ++this.loadRowsRequestId;
        this.esciService.getAggregatorSignalRows(objectCode, architecture, powerType, keyword).subscribe(rows => {
            if (requestId !== this.loadRowsRequestId
                || this.activeObjectCodeSignal() !== objectCode
                || this.activeArchitectureSignal() !== architecture
                || this.activePowerTypeSignal() !== powerType
                || this.activeKeywordSignal() !== keyword) {
                return;
            }
            const loaded = (rows || []).map(row => this.tagLoaded(row));
            this.refreshGrid(loaded as any);
        });
    }

    openCreateDialog(): void {
        this.dialogObjectCode = '';
        this.dialogArchitecture = DEFAULT_ICD_PLATFORM;
        this.dialogPowerType = '';
        this.dialogKeyword = '';
        this.dialogCopySourceLabel = '';
        this.createDialogVisibleSignal.set(true);
        if (!this.createObjectCodesLoaded && !this.createObjectCodesLoading) {
            this.loadCreateObjectCodes();
        } else if (this.createObjectCodes.length) {
            this.filterCreateObjectCodes({query: ''});
        }
    }

    closeCreateDialog(): void {
        this.createDialogVisibleSignal.set(false);
        this.dialogObjectCode = '';
        this.dialogArchitecture = DEFAULT_ICD_PLATFORM;
        this.dialogPowerType = '';
        this.dialogKeyword = '';
        this.dialogCopySourceLabel = '';
    }

    confirmCreateDialog(): void {
        const objectCode = String(this.dialogObjectCode ?? '').trim();
        const architecture = normalizeArchitecture(this.dialogArchitecture);
        const powerType = this.dialogPowerType;
        const keyword = normalizeKeyword(this.dialogKeyword);
        if (!objectCode || !architecture || !powerType || !this.dialogCopySourceLabel) {
            this.messageService.processError('Object Code, Architecture, Power Type, and Copy from are all required.');
            return;
        }
        if (keyExists(this.keysSignal(), objectCode, architecture, powerType, keyword)) {
            this.messageService.processError(
                `Key ${formatKeyLabel({objectCode, architecture, powerType, keyword, rowCount: 0})} already exists. Use browse selectors to edit it.`);
            return;
        }
        const source = this.keysSignal().find(key => this.keyLabel(key) === this.dialogCopySourceLabel);
        if (!source) {
            this.messageService.processError('Selected copy source is no longer available.');
            return;
        }
        this.browseObjectCodeSignal.set('');
        this.browseArchitectureSignal.set('');
        this.browsePowerTypeSignal.set('');
        this.browseKeywordSignal.set('');
        this.activeObjectCodeSignal.set(objectCode);
        this.activeArchitectureSignal.set(architecture);
        this.activePowerTypeSignal.set(powerType);
        this.activeKeywordSignal.set(keyword);
        this.esciService.getAggregatorSignalRows(source.objectCode,
            normalizeArchitecture(source.architecture) || DEFAULT_ICD_PLATFORM,
            source.powerType, normalizeKeyword(source.keyword)).subscribe(rows => {
            this.applyCopyFromRows(rows || []);
            this.createDraftSnapshotSignal.set(this.cloneRowsForSnapshot(this.rowData as AggregatorSignalRow[]));
            this.createModeSignal.set(true);
            this.closeCreateDialog();
        });
    }

    applyCopyFromRows(source: AggregatorSignalRow[]): void {
        const targetObjectCode = this.activeObjectCodeSignal();
        const targetArchitecture = this.activeArchitectureSignal();
        const targetPowerType = this.activePowerTypeSignal();
        const targetKeyword = this.activeKeywordSignal();
        const cloned = source.map(src => {
            const copy: AggregatorSignalRow = {...src};
            delete copy.id;
            copy.objectCode = targetObjectCode;
            copy.architecture = targetArchitecture;
            copy.powerType = targetPowerType as PowerType;
            copy.keyword = targetKeyword;
            copy.ioTypeDesignator = this.ioTypeNameForId(copy.fkIoTypeDesignatorId) || copy.ioTypeDesignator;
            copy.terminalSize = this.terminalSizeNameForId(copy.fkTerminalSizeId) || copy.terminalSize;
            copy.terminalPlating = this.platingNameForId(copy.fkPlatingId) || copy.terminalPlating;
            copy.state = 'new';
            copy.changes = new Map<string, string>();
            copy.originalRowData = undefined;
            return copy;
        });
        this.refreshGrid(cloned as any);
        this.setDirty(true);
    }

    onCopyRow(params: any): void {
        const source = params?.data as AggregatorSignalRow;
        if (!source) {
            return;
        }
        const copy: AggregatorSignalRow = {...source};
        delete copy.id;
        (copy as any).id = -1;
        copy.objectCode = this.activeObjectCodeSignal();
        copy.architecture = this.activeArchitectureSignal();
        copy.keyword = this.activeKeywordSignal();
        copy.powerType = this.activePowerTypeSignal() as PowerType;
        copy.state = 'new';
        copy.changes = new Map<string, string>();
        copy.originalRowData = undefined;
        const index = this.rowData.indexOf(source);
        const next = [...this.rowData];
        next.splice(index >= 0 ? index + 1 : next.length, 0, copy);
        this.rowData = next;
        this.setDirty(true);
        this.refreshGrid(this.rowData as any);
    }

    confirmDeleteTable(): void {
        if (!this.canDeleteTable() || this.saving) {
            return;
        }
        const label = formatKeyLabel({
            objectCode: this.activeObjectCodeSignal(),
            architecture: this.activeArchitectureSignal(),
            powerType: this.activePowerTypeSignal(),
            keyword: this.activeKeywordSignal(),
            rowCount: 0
        });
        this.confirmationService.confirm({
            key: 'deleteTableConfirmation',
            message: `Delete the entire signal table for ${label}? This cannot be undone.`,
            header: 'Delete Table',
            icon: 'pi pi-exclamation-triangle',
            defaultFocus: 'accept',
            acceptButtonProps: {severity: 'danger'},
            accept: () => this.deleteTable()
        });
    }

    deleteTable(): void {
        if (!this.canDeleteTable() || this.saving) {
            return;
        }
        this.saving = true;
        this.esciService.deleteAggregatorSignalTable(
            this.activeObjectCodeSignal(), this.activeArchitectureSignal(),
            this.activePowerTypeSignal() as string, this.activeKeywordSignal()).pipe(
            finalize(() => {
                this.saving = false;
            })
        ).subscribe({
            next: () => {
                this.messageService.processSuccess('Aggregator Signal table deleted.');
                this.clearActiveKeyGrid();
                this.browseObjectCodeSignal.set('');
                this.browseArchitectureSignal.set('');
                this.browsePowerTypeSignal.set('');
                this.browseKeywordSignal.set('');
                this.refreshKeys().subscribe();
            },
            error: (err: any) => {
                this.messageService.processError(err?.error?.message || 'Delete Table failed.');
            }
        });
    }

    exportHistoryAll(): void {
        if (!this.canExportHistoryAll() || this.exporting) {
            this.messageService.processError('User does not have permission to download all history.');
            return;
        }
        this.setExporting(true);
        this.esciService.getAggregatorSignalHistoryAll()
            .pipe(take(1), finalize(() => this.setExporting(false)))
            .subscribe({
                next: (data: ArrayBuffer) => {
                    this.setExporting(false);
                    if (!data || data.byteLength < 1) {
                        this.messageService.processError('No history to export.');
                        return;
                    }
                    const fileName = `AggregatorSignalHistoryAll-${CommonHelperService.getTimeStampForReport()}.xlsx`;
                    CommonHelperService.downloadFile(new Blob([data], {type: 'application/octet-stream;charset=utf-8'}), fileName);
                },
                error: (err: any) => {
                    this.messageService.processError(err?.error?.message || 'All-history export failed.');
                }
            });
    }

    refreshKeys() {
        return this.esciService.getAggregatorSignalKeys().pipe(
            tap(keys => this.keysSignal.set(keys || []))
        );
    }

    private exitCreateModeAndSelectKey(objectCode: string, architecture: string, powerType: PowerType | '',
                                       keyword: string): void {
        this.createModeSignal.set(false);
        this.createDraftSnapshotSignal.set([]);
        this.browseObjectCodeSignal.set(objectCode);
        this.browseArchitectureSignal.set(architecture);
        this.browsePowerTypeSignal.set(powerType);
        this.browseKeywordSignal.set(keyword);
        this.activeObjectCodeSignal.set(objectCode);
        this.activeArchitectureSignal.set(architecture);
        this.activePowerTypeSignal.set(powerType);
        this.activeKeywordSignal.set(keyword);
        this.loadExistingKey();
    }

    private clearActiveKeyGrid(): void {
        this.activeObjectCodeSignal.set('');
        this.activeArchitectureSignal.set('');
        this.activePowerTypeSignal.set('');
        this.activeKeywordSignal.set('');
        this.refreshGrid([] as any);
        this.setDirty(false);
    }

    private cloneRowsForSnapshot(rows: AggregatorSignalRow[]): AggregatorSignalRow[] {
        return (rows || []).map(row => {
            const copy: AggregatorSignalRow = {...row};
            copy.changes = new Map(row.changes || []);
            copy.originalRowData = row.originalRowData ? {...row.originalRowData} : undefined;
            return copy;
        });
    }

    private loadIoTypeDesignators(): void {
        this.esciService.getAggregatorSignalIoTypes().subscribe(options => {
            this.ioTypeNameById.clear();
            this.ioTypeIdByName.clear();
            this.ioTypeDesignatorOptions.length = 0;
            for (const option of options || []) {
                this.ioTypeDesignatorOptions.push(option);
                this.ioTypeNameById.set(option.id, option.ioTypeDesignator);
                this.ioTypeIdByName.set(option.ioTypeDesignator, option.id);
            }
            for (const row of this.rowData as AggregatorSignalRow[]) {
                row.ioTypeDesignator = this.ioTypeNameForId(row.fkIoTypeDesignatorId);
            }
            this.updateIoTypeColumnWidth();
            if (this.isGridApiAlive(this.gridApi)) {
                this.gridApi.refreshCells({force: true});
            }
        });
    }

    private updateIoTypeColumnWidth(): void {
        const ioTypeCol = this.cols.find(c => 'field' in c && c.field === 'ioTypeDesignator') as ColDef | undefined;
        if (!ioTypeCol) {
            return;
        }
        let maxLength = 12;
        for (const option of this.ioTypeDesignatorOptions) {
            maxLength = Math.max(maxLength, (option.ioTypeDesignator || '').length);
        }
        ioTypeCol.minWidth = maxLength * 5 + 40;
        if (this.isGridApiAlive(this.gridApi)) {
            this.gridApi.setGridOption('columnDefs', this.cols);
        }
    }

    private ioTypeNameForId(id: number | null | undefined): string {
        return id != null && this.ioTypeNameById.has(id) ? this.ioTypeNameById.get(id)! : '';
    }

    private ioTypeDesignatorDisplayValue(value: unknown): string {
        if (typeof value === 'string') {
            return value.trim();
        }
        if (value && typeof value === 'object' && 'ioTypeDesignator' in (value as IoTypeDesignatorOption)) {
            return String((value as IoTypeDesignatorOption).ioTypeDesignator ?? '').trim();
        }
        return '';
    }

    private loadTerminalSizes(): void {
        this.esciService.getAggregatorSignalTerminalSizes().subscribe(options => {
            this.terminalSizeNameById.clear();
            this.terminalSizeIdByName.clear();
            this.terminalSizeOptions.length = 0;
            for (const option of options || []) {
                this.terminalSizeOptions.push(option);
                this.terminalSizeNameById.set(option.id, option.size);
                this.terminalSizeIdByName.set(option.size, option.id);
            }
            for (const row of this.rowData as AggregatorSignalRow[]) {
                row.terminalSize = this.terminalSizeNameForId(row.fkTerminalSizeId);
            }
            if (this.isGridApiAlive(this.gridApi)) {
                this.gridApi.refreshCells({force: true});
            }
        });
    }

    private terminalSizeNameForId(id: number | null | undefined): string {
        return id != null && this.terminalSizeNameById.has(id) ? this.terminalSizeNameById.get(id)! : '';
    }

    private terminalSizeDisplayValue(value: unknown): string {
        if (typeof value === 'string') {
            return value.trim();
        }
        if (value && typeof value === 'object' && 'size' in (value as TerminalSizeOption)) {
            return String((value as TerminalSizeOption).size ?? '').trim();
        }
        return '';
    }

    private loadPwmGroups(): void {
        this.esciService.getAggregatorSignalPwmGroups().subscribe(options => {
            this.replaceStringDropdownOptions(this.pwmGroupDropdownOptions, options, 'pwmGroup');
        });
    }

    private loadVRefMappings(): void {
        this.esciService.getAggregatorSignalVRefMappings().subscribe(options => {
            this.replaceStringDropdownOptions(this.vRefMappingDropdownOptions, options, 'vRefMapping');
        });
    }

    private loadFlexibleDiscreteOutputs(): void {
        this.esciService.getAggregatorSignalFlexibleDiscreteOutputs().subscribe(options => {
            this.flexibleDiscreteOutputOptions.length = 0;
            for (const option of options || []) {
                const acro = String(option?.acro ?? '').trim();
                if (!acro) {
                    continue;
                }
                this.flexibleDiscreteOutputOptions.push({
                    id: acro,
                    flexibleDiscreteOutputs: acro,
                    label: String(option.status ?? acro).trim() || acro
                });
            }
            if (this.isGridApiAlive(this.gridApi)) {
                this.gridApi.refreshCells({force: true});
            }
        });
    }

    private replaceStringDropdownOptions<K extends string>(
            target: Array<{id: string} & Record<K, string>>,
            options: EioEnumOption[] | null | undefined,
            valueKey: K): void {
        target.length = 0;
        for (const option of options || []) {
            const acro = String(option?.acro ?? '').trim();
            if (!acro) {
                continue;
            }
            target.push({id: acro, [valueKey]: acro} as {id: string} & Record<K, string>);
        }
        if (this.isGridApiAlive(this.gridApi)) {
            this.gridApi.refreshCells({force: true});
        }
    }

    private loadPlatings(): void {
        this.esciService.getAggregatorSignalPlatings().subscribe(options => {
            this.platingNameById.clear();
            this.platingIdByName.clear();
            this.platingOptions.length = 0;
            for (const option of options || []) {
                this.platingOptions.push(option);
                this.platingNameById.set(option.id, option.plating);
                this.platingIdByName.set(option.plating, option.id);
            }
            for (const row of this.rowData as AggregatorSignalRow[]) {
                row.terminalPlating = this.platingNameForId(row.fkPlatingId);
            }
            if (this.isGridApiAlive(this.gridApi)) {
                this.gridApi.refreshCells({force: true});
            }
        });
    }

    private platingNameForId(id: number | null | undefined): string {
        return id != null && this.platingNameById.has(id) ? this.platingNameById.get(id)! : '';
    }

    private platingDisplayValue(value: unknown): string {
        if (typeof value === 'string') {
            return value.trim();
        }
        if (value && typeof value === 'object' && 'plating' in (value as PlatingOption)) {
            return String((value as PlatingOption).plating ?? '').trim();
        }
        return '';
    }

    private tagLoaded(row: AggregatorSignalRow): AggregatorSignalRow {
        row.ioTypeDesignator = this.ioTypeNameForId(row.fkIoTypeDesignatorId);
        row.terminalSize = this.terminalSizeNameForId(row.fkTerminalSizeId);
        row.terminalPlating = this.platingNameForId(row.fkPlatingId);
        row.state = 'saved';
        row.changes = new Map<string, string>();
        row.originalRowData = {...row};
        return row;
    }

    onCellValueChanged(event: CellValueChangedEvent): void {
        const field = event?.colDef?.field;
        const editedRow = event?.data as AggregatorSignalRow;
        if (!editedRow) {
            return;
        }
        if (field === 'partNumber') {
            const partNumber = String(event.newValue ?? editedRow.partNumber ?? '').trim();
            editedRow.partNumber = partNumber;
            const connector = (editedRow.connector || '').trim();
            if (connector) {
                for (const row of this.rowData as AggregatorSignalRow[]) {
                    if ((row.connector || '').trim() === connector) {
                        row.partNumber = partNumber;
                    }
                }
            }
        }
        if (field === 'ioTypeDesignator') {
            const picked = event.newValue ?? editedRow.ioTypeDesignator;
            const name = this.ioTypeDesignatorDisplayValue(picked);
            editedRow.ioTypeDesignator = name;
            if (picked && typeof picked === 'object' && picked.id != null) {
                editedRow.fkIoTypeDesignatorId = picked.id;
            } else {
                editedRow.fkIoTypeDesignatorId = this.ioTypeIdByName.has(name) ? this.ioTypeIdByName.get(name)! : null;
            }
        }
        if (field === 'terminalSize') {
            const picked = event.newValue ?? editedRow.terminalSize;
            const name = this.terminalSizeDisplayValue(picked);
            editedRow.terminalSize = name;
            if (picked && typeof picked === 'object' && (picked as TerminalSizeOption).id != null) {
                editedRow.fkTerminalSizeId = (picked as TerminalSizeOption).id;
            } else {
                editedRow.fkTerminalSizeId = this.terminalSizeIdByName.has(name) ? this.terminalSizeIdByName.get(name)! : null;
            }
        }
        if (field === 'connector') {
            editedRow.connector = String(event.newValue ?? editedRow.connector ?? '');
        }
        if (field === 'connectorPin') {
            editedRow.connectorPin = String(event.newValue ?? editedRow.connectorPin ?? '');
        }
        if (field === 'terminalPlating') {
            const picked = event.newValue ?? editedRow.terminalPlating;
            const name = this.platingDisplayValue(picked);
            editedRow.terminalPlating = name;
            if (picked && typeof picked === 'object' && (picked as PlatingOption).id != null) {
                editedRow.fkPlatingId = (picked as PlatingOption).id;
            } else {
                editedRow.fkPlatingId = this.platingIdByName.has(name) ? this.platingIdByName.get(name)! : null;
            }
        }
        if (field === 'flexibleDiscreteOutputs') {
            editedRow.flexibleDiscreteOutputs = this.flexibleDiscreteAcro(event.newValue ?? editedRow.flexibleDiscreteOutputs);
        }
        if (field === 'pwmGroup') {
            editedRow.pwmGroup = this.stringOptionValue(event.newValue, 'pwmGroup');
        }
        if (field === 'vRefMapping') {
            editedRow.vRefMapping = this.stringOptionValue(event.newValue, 'vRefMapping');
        }
        this.updateDataChanged(this.rowData as any);
        if (this.isGridApiAlive(this.gridApi)) {
            this.gridApi.refreshCells({force: true});
        }
    }

    private buildColumns(): void {
        // No column sets `editable` itself: a per-column value overrides defaultColDef, which is where
        // the framework applies the adminPermissionUrl check that keeps the grid read-only for viewers.
        const cols: ColDef[] = [
            {
                headerName: 'Actions', colId: 'actions', editable: false, filter: false, sortable: false,
                lockPosition: true, suppressMovable: true, minWidth: 90, maxWidth: 110,
                cellRenderer: ActionsCellRendererComponent,
                cellRendererParams: {
                    onDeleteClick: (function (params: any): void {
                        this.onDeleteClick(params, this.rowData, (updatedData) => this.refreshGrid(updatedData));
                        this.setDirty(true);
                    }).bind(this),
                    onCopyClick: this.onCopyRow.bind(this),
                    fieldForDeleteMessage: 'connectorPin'
                }
            },
            {field: 'connector', headerName: 'Connector', comparator: compareNaturalText},
            {field: 'connectorPin', headerName: 'Connector Pin', comparator: compareNaturalText},
            {field: 'portSignalName', headerName: 'Port/Signal Name'},
            {
                field: 'terminalSize', headerName: 'Minimum Terminal Size',
                cellDataType: 'text',
                valueFormatter: (params) => this.terminalSizeDisplayValue(params?.value),
                cellEditor: ObjectDropdownCellEditorComponent,
                cellEditorParams: {
                    availableOptions: this.terminalSizeOptions,
                    allowedValueKey: 'size',
                    maxSelectionCount: 1,
                    memberType: 'objectDropdown',
                    isListManagerStructure: true,
                    filter: true
                },
                valueSetter: (params: any) => {
                    params.data[params.colDef.field] = this.terminalSizeDisplayValue(params.newValue);
                    return true;
                },
                cellEditorSelector: () => ({
                    component: ObjectDropdownCellEditorComponent,
                    params: {
                        availableOptions: this.terminalSizeOptions,
                        allowedValueKey: 'size',
                        maxSelectionCount: 1,
                        memberType: 'objectDropdown',
                        isListManagerStructure: true,
                        filter: true
                    }
                })
            },
            {field: 'swChannelId', headerName: 'SW Channel ID'},
            {
                field: 'ioTypeDesignator', headerName: 'IO Type',
                cellDataType: 'text',
                valueFormatter: (params) => this.ioTypeDesignatorDisplayValue(params?.value),
                cellEditor: ObjectDropdownCellEditorComponent,
                cellEditorParams: {
                    availableOptions: this.ioTypeDesignatorOptions,
                    allowedValueKey: 'ioTypeDesignator',
                    maxSelectionCount: 1,
                    memberType: 'objectDropdown',
                    isListManagerStructure: true,
                    filter: true
                },
                valueSetter: (params: any) => {
                    params.data[params.colDef.field] = this.ioTypeDesignatorDisplayValue(params.newValue);
                    return true;
                },
                cellEditorSelector: () => ({
                    component: ObjectDropdownCellEditorComponent,
                    params: {
                        availableOptions: this.ioTypeDesignatorOptions,
                        allowedValueKey: 'ioTypeDesignator',
                        maxSelectionCount: 1,
                        memberType: 'objectDropdown',
                        isListManagerStructure: true,
                        filter: true
                    }
                })
            },
            {field: 'partNumber', headerName: 'Part Number'},
            this.buildStringDropdownColumn('pwmGroup', 'PWM Group', this.pwmGroupDropdownOptions, 'pwmGroup'),
            this.buildStringDropdownColumn('vRefMapping', 'VRef Mapping', this.vRefMappingDropdownOptions, 'vRefMapping'),
            {
                field: 'flexibleDiscreteOutputs', headerName: 'Flexible Discrete Outputs',
                cellDataType: 'text',
                valueFormatter: (params) => this.flexibleDiscreteDisplayValue(params?.value),
                cellEditor: ObjectDropdownCellEditorComponent,
                cellEditorParams: {
                    availableOptions: this.flexibleDiscreteOutputOptions,
                    allowedValueKey: 'flexibleDiscreteOutputs',
                    maxSelectionCount: 1,
                    memberType: 'objectDropdown',
                    isListManagerStructure: true,
                    filter: true,
                    allowClear: true
                },
                valueSetter: (params: any) => {
                    params.data[params.colDef.field] = this.flexibleDiscreteAcro(params.newValue);
                    return true;
                },
                cellEditorSelector: () => ({
                    component: ObjectDropdownCellEditorComponent,
                    params: {
                        availableOptions: this.flexibleDiscreteOutputOptions,
                        allowedValueKey: 'flexibleDiscreteOutputs',
                        maxSelectionCount: 1,
                        memberType: 'objectDropdown',
                        isListManagerStructure: true,
                        filter: true,
                        allowClear: true
                    }
                })
            },
            {
                field: 'terminalPlating', headerName: 'Terminal Plating',
                cellDataType: 'text',
                valueFormatter: (params) => this.platingDisplayValue(params?.value),
                cellEditor: ObjectDropdownCellEditorComponent,
                cellEditorParams: {
                    availableOptions: this.platingOptions,
                    allowedValueKey: 'plating',
                    maxSelectionCount: 1,
                    memberType: 'objectDropdown',
                    isListManagerStructure: true,
                    filter: true
                },
                valueSetter: (params: any) => {
                    params.data[params.colDef.field] = this.platingDisplayValue(params.newValue);
                    return true;
                },
                cellEditorSelector: () => ({
                    component: ObjectDropdownCellEditorComponent,
                    params: {
                        availableOptions: this.platingOptions,
                        allowedValueKey: 'plating',
                        maxSelectionCount: 1,
                        memberType: 'objectDropdown',
                        isListManagerStructure: true,
                        filter: true
                    }
                })
            }
        ];
        this.cols = cols;
    }

    private buildStringDropdownColumn(field: string, headerName: string,
                                      options: Array<Record<string, string>>,
                                      allowedValueKey: string): ColDef {
        const editorParams = {
            availableOptions: options,
            allowedValueKey,
            maxSelectionCount: 1,
            memberType: 'objectDropdown',
            isListManagerStructure: true,
            filter: true,
            allowClear: true
        };
        return {
            field, headerName,
            cellDataType: 'text',
            valueFormatter: (params) => this.stringOptionValue(params?.value, allowedValueKey),
            cellEditor: ObjectDropdownCellEditorComponent,
            cellEditorParams: editorParams,
            valueSetter: (params: any) => {
                params.data[params.colDef.field] = this.stringOptionValue(params.newValue, allowedValueKey);
                return true;
            },
            cellEditorSelector: () => ({
                component: ObjectDropdownCellEditorComponent,
                params: editorParams
            })
        };
    }

    private stringOptionValue(value: unknown, key: string): string {
        if (typeof value === 'string') {
            return value.trim();
        }
        if (value && typeof value === 'object' && key in (value as Record<string, unknown>)) {
            return String((value as Record<string, unknown>)[key] ?? '').trim();
        }
        return '';
    }

    private flexibleDiscreteAcro(value: unknown): string {
        if (value && typeof value === 'object') {
            const obj = value as {id?: string; flexibleDiscreteOutputs?: string; label?: string};
            if (obj.flexibleDiscreteOutputs) {
                return obj.flexibleDiscreteOutputs;
            }
            if (obj.id === 'Y' || obj.id === 'N') {
                return obj.id;
            }
            const label = String(obj.label ?? '').trim().toLowerCase();
            if (label === 'yes') {
                return 'Y';
            }
            if (label === 'no') {
                return 'N';
            }
        }
        const raw = String(value ?? '').trim();
        if (raw.toLowerCase() === 'yes') {
            return 'Y';
        }
        if (raw.toLowerCase() === 'no') {
            return 'N';
        }
        return raw;
    }

    private flexibleDiscreteDisplayValue(value: unknown): string {
        const acro = this.flexibleDiscreteAcro(value);
        const match = this.flexibleDiscreteOutputOptions.find(option =>
            option.flexibleDiscreteOutputs === acro || option.id === acro);
        if (match?.label) {
            return match.label;
        }
        if (acro === 'Y') {
            return 'Yes';
        }
        if (acro === 'N') {
            return 'No';
        }
        return acro;
    }

    save(): void {
        if (!this.adminPermission()) {
            this.messageService.processError('User does not have permission to edit the Aggregator Signal Table.');
            return;
        }
        if (!this.areRowsValid(this.rowData as any)) {
            return;
        }
        const activeRows = (this.rowData as AggregatorSignalRow[]).filter(row => row.state !== 'deleted');
        const wasCreateMode = this.createModeSignal();
        this.saving = true;
        this.esciService.saveAggregatorSignal(this.activeObjectCodeSignal(),
            this.activeArchitectureSignal(),
            this.activePowerTypeSignal() as string,
            this.activeKeywordSignal(),
            activeRows).pipe(
            finalize(() => {
                this.saving = false;
            })
        ).subscribe({
            next: () => {
                this.messageService.processSuccess('Aggregator Signal saved successfully.');
                this.markRowsSavedAfterSave();
                if (wasCreateMode) {
                    this.refreshKeys().subscribe(() => {
                        this.exitCreateModeAndSelectKey(
                            this.activeObjectCodeSignal(), this.activeArchitectureSignal(),
                            this.activePowerTypeSignal(), this.activeKeywordSignal());
                    });
                } else {
                    this.loadExistingKey();
                }
            },
            error: (err: any) => {
                this.messageService.processError(err?.error?.message || 'Save failed.');
            }
        });
    }

    private markRowsSavedAfterSave(): void {
        const activeRows = (this.rowData as AggregatorSignalRow[]).filter(row => row.state !== 'deleted');
        for (const row of activeRows) {
            this.tagLoaded(row);
        }
        this.setDirty(false);
        this.rowsInvalid = false;
        if (this.isGridApiAlive(this.gridApi)) {
            this.gridApi.refreshCells({force: true});
        }
    }

    resetChanges(): void {
        if (this.createModeSignal()) {
            this.cancelCreateMode();
            return;
        }
        this.loadExistingKey();
    }

    private cancelCreateMode(): void {
        this.createModeSignal.set(false);
        this.createDraftSnapshotSignal.set([]);
        this.clearActiveKeyGrid();
        this.rowsInvalid = false;
    }

    ngAfterViewChecked(): void {
        if (this.permission() && this.canExport() && !this.downloadButtons.isConnected) {
            this.ensureDownloadButtonsInFooter();
        }
        this.downloadButtons.updateDisabledState();
    }

    protected override afterPrimaryGridReady(_params: GridReadyEvent): void {
        this.ensureDownloadButtonsInFooter();
    }

    get canExportReport(): boolean {
        return this.canExport()
            && !this.exporting
            && (this.rowData as AggregatorSignalRow[]).some(row => row.state !== 'deleted');
    }

    /** History is an audit trail, so it follows the write gate rather than the page's read gate. */
    get canExportHistory(): boolean {
        return this.canExport()
            && this.adminPermission()
            && !this.exporting
            && !!this.activeObjectCodeSignal()
            && !!this.activeArchitectureSignal()
            && !!this.activePowerTypeSignal()
            && !this.createModeSignal();
    }


    exportReport(): void {
        if (!this.canExport()) {
            this.messageService.processError('User does not have export permission.');
            return;
        }
        if (!this.isGridApiAlive(this.gridApi) || this.gridApi.getDisplayedRowCount() < 1) {
            this.messageService.processError('There are no rows to export.');
            return;
        }
        this.excelService.exportGridDataAsExcelFile(this.gridApi, this.getReportFileNamePrefix(), this.getExportTabName());
    }

    onIcdCompareKeydown(event: KeyboardEvent): void {
        handleIcdCompareKeydown(event, () => this.exportIcdCompare());
    }

    exportIcdCompare(): void {
        if (!this.canExportIcdCompare() || this.saving || this.exporting) {
            this.messageService.processError('User does not have permission to download the ICD compare, or no table is selected.');
            return;
        }
        const objectCode = this.activeObjectCodeSignal();
        const powerType = this.activePowerTypeSignal() as string;
        subscribeIcdCompareDownload(
            this.esciService.getAggregatorSignalIcdCompare(objectCode,
                this.activeArchitectureSignal(), powerType,
                this.activeKeywordSignal()),
            {
                setExporting: value => this.setExporting(value),
                fileName: `${this.getExportFileNamePrefix('AggregatorSignalIcdCompare')}-${CommonHelperService.getTimeStampForReport()}.xlsx`,
                onEmpty: message => this.messageService.processError(message),
                onError: message => this.messageService.processError(message)
            }
        );
    }

    exportHistory(): void {
        if (!this.canExport() || !this.adminPermission()) {
            this.messageService.processError('User does not have permission to download history.');
            return;
        }
        if (!this.activeObjectCodeSignal() || !this.activePowerTypeSignal()) {
            this.messageService.processError('Select Object Code and Power Type first.');
            return;
        }
        this.setExporting(true);
        this.esciService.getAggregatorSignalHistory(this.activeObjectCodeSignal(),
            this.activeArchitectureSignal(),
            this.activePowerTypeSignal() as string,
            this.activeKeywordSignal())
            .pipe(take(1), finalize(() => this.setExporting(false)))
            .subscribe({
            next: (data: ArrayBuffer) => {
                this.setExporting(false);
                if (!data || data.byteLength < 1) {
                    this.messageService.processError('No history to export for this key yet.');
                    return;
                }
                const fileName = `${this.getHistoryFileNamePrefix()}-${CommonHelperService.getTimeStampForReport()}.xlsx`;
                CommonHelperService.downloadFile(new Blob([data], {type: 'application/octet-stream;charset=utf-8'}), fileName);
            },
            error: (err: any) => {
                this.messageService.processError(err?.error?.message || 'History export failed.');
            }
        });
    }

    private setExporting(value: boolean): void {
        setExportingWithDetectChanges(this, this.changeDetectorRef, value);
    }

    private ensureDownloadButtonsInFooter(): void {
        if (!this.permission() || !this.canExport()) {
            return;
        }
        this.downloadButtons.ensureInFooter(this.agGridHost?.nativeElement,
            (node, gridHost) => this.insertNodeIntoAgGridPaginationPanel(node, gridHost));
    }

    private getReportFileNamePrefix(): string {
        return this.getExportFileNamePrefix('AggregatorSignalReport');
    }

    private getHistoryFileNamePrefix(): string {
        return this.getExportFileNamePrefix('AggregatorSignalHistory');
    }

    private getExportFileNamePrefix(baseName: string): string {
        const objectCode = (this.activeObjectCodeSignal() || '').trim();
        const architecture = (this.activeArchitectureSignal() || '').trim();
        const powerType = (this.activePowerTypeSignal() || '').trim();
        const keyword = (this.activeKeywordSignal() || '').trim();
        if (objectCode && architecture && powerType) {
            const suffix = keyword
                ? `${objectCode}-${architecture}-${powerType}-${keyword}`
                : `${objectCode}-${architecture}-${powerType}`;
            return `${baseName}-${suffix}`;
        }
        return baseName;
    }

    private getExportTabName(): string {
        const objectCode = (this.activeObjectCodeSignal() || '').trim();
        const architecture = (this.activeArchitectureSignal() || '').trim();
        const powerType = (this.activePowerTypeSignal() || '').trim();
        const keyword = (this.activeKeywordSignal() || '').trim();
        let tabName = 'Aggregator Signal';
        if (objectCode && architecture && powerType) {
            tabName = keyword
                ? `${objectCode} - ${architecture} - ${powerType} - ${keyword}`
                : `${objectCode} - ${architecture} - ${powerType}`;
        }
        return ExcelService.sanitizeExcelSheetName(tabName);
    }
}
